import { IN_OPERATOR, NOT_IN_OPERATOR } from './reportsConsts';
import { getSitePublicId } from "./apps";
export const APP_SITE_ID_DIMENSION = 'app_site_id';
export const AGENCY_PERMISSION_DIMENSIONS = [APP_SITE_ID_DIMENSION, 'base_source'];

const normalizePermissionValues = values => {
    if (!values?.length) {
        return [];
    }
    return values.map(v => String(v));
};

const getPermissionFromRole = (agencyRole, dimension) => {
    if (!agencyRole) {
        return null;
    }
    return agencyRole.row?.find(r => r.dimension === dimension) || null;
};

const checkValueAccess = (value, permissionValues, operator) => {
    const isInList = permissionValues.includes(String(value));
    return operator === IN_OPERATOR ? isInList : !isInList;
};

const filterAgenciesByDimension = (agencies, selectedValues, dimension, getValueFromItem, emptyValuesHandler = null) => {
    if (!selectedValues?.length) {
        return agencies.map(agency => ({ agency, hasPermission: true }));
    }

    const normalizedValues = selectedValues.map(item => getValueFromItem(item));
    return agencies.map(agency => {
        const agencyRole = agency?.role;
        if (!agencyRole) {
            return { agency, hasPermission: true };
        }

        const permission = getPermissionFromRole(agencyRole, dimension);
        if (!permission) {
            return { agency, hasPermission: true };
        }

        const { operator, values } = permission;
        const permissionValues = normalizePermissionValues(values);
        if (!permissionValues.length) {
            if (emptyValuesHandler) {
                return {
                    agency,
                    hasPermission: emptyValuesHandler(operator),
                };
            }
            return {
                agency,
                hasPermission: operator === NOT_IN_OPERATOR,
            };
        }

        const hasAccess = normalizedValues.some(value => checkValueAccess(value, permissionValues, operator));

        return { agency, hasPermission: hasAccess };
    });
};

export const filterAgenciesByAppPermissions = (agencies, selectedAppSites, dimension = APP_SITE_ID_DIMENSION) => {
    return filterAgenciesByDimension(agencies, selectedAppSites, dimension, getSitePublicId);
};
const filterItemsByDimension = (items, agencyRole, dimension, getItemValue) => {
    if (!agencyRole || !items) {
        return items;
    }

    const permission = getPermissionFromRole(agencyRole, dimension);
    if (!permission) {
        return items;
    }

    const { operator, values } = permission;
    const permissionValues = normalizePermissionValues(values);

    if (!permissionValues.length) {
        return operator === IN_OPERATOR ? [] : items;
    }

    return items.filter(item => {
        const itemValue = getItemValue(item);
        return checkValueAccess(itemValue, permissionValues, operator);
    });
};

const filterSitesByDimension = (sites, agencyRole, dimension, getSiteValue) => {
    return filterItemsByDimension(sites, agencyRole, dimension, getSiteValue);
};

export const filterSourcesByAgencyPermissions = (sources, agencyRole) => {
    return filterItemsByDimension(sources, agencyRole, 'base_source', source => String(source?.value || source?.name || ''));
};

export const filterSitesByAppSitePermissions = (sites, agencyRole, dimension = APP_SITE_ID_DIMENSION) => {
    return filterSitesByDimension(sites, agencyRole, dimension, getSitePublicId);
};

export const filterAgenciesByPartnerPermissions = (agencies, partnerName, configuredPartners) => {
    if (!partnerName) {
        return agencies.map(agency => ({ agency, hasPermission: true }));
    }

    // Special case: "not_in: []" means all partners are allowed
    return filterAgenciesByDimension(
        agencies,
        [partnerName],
        'base_source',
        partner => String(partner),
        operator => operator === NOT_IN_OPERATOR // emptyValuesHandler: only allow if NOT_IN_OPERATOR
    );
};

export const checkAgencyPermissionsForLinkSites = (orgAgencies, agencyId, linkAppSites, partnerName = null) => {
    if (!orgAgencies || !agencyId || !linkAppSites?.length) {
        // Default to true if we can't check (conservative approach)
        return true;
    }

    // Check app site permissions
    const filteredAgenciesByApp = filterAgenciesByAppPermissions(orgAgencies, linkAppSites);
    const agencyAppResult = filteredAgenciesByApp.find(({ agency }) => agency.agency_id === agencyId);
    const hasAppSitePermission = agencyAppResult?.hasPermission ?? true;

    // Check source/partner permissions if partnerName is provided
    let hasPartnerPermission = true;
    if (partnerName) {
        const filteredAgenciesByPartner = filterAgenciesByPartnerPermissions(orgAgencies, partnerName, []);
        const agencyPartnerResult = filteredAgenciesByPartner.find(({ agency }) => agency.agency_id === agencyId);
        hasPartnerPermission = agencyPartnerResult?.hasPermission ?? true;
    }

    // Agency must have permissions to BOTH app site AND partner (if provided)
    return hasAppSitePermission && hasPartnerPermission;
};

export const filterAppsByAgencyPermissions = (apps, agencyRole) => {
    if (!apps?.length) {
        return [];
    }

    const appsWithFilteredSites = apps.map(app => ({
        ...app,
        sites: filterSitesByAppSitePermissions(app.sites, agencyRole),
    }));

    return appsWithFilteredSites.filter(app => app.sites.length > 0);
};


export const getAgencyRole = (agencies, agencyId) => {
    if (!agencyId) {
        return null;
    }

    const agency = agencies?.find(a => a.agency_id === agencyId);
    return agency?.role || null;
};